"use client"
import { extractData, getEcommColors, getShowroomAll } from "api"
import { trackBuildMazdaCTA } from "common/analytics"
import NextLink from "common/bit/NextLink"
import { keepOnlyUtmParams } from "common/bit/utils/utmParams"
import { buildShopLink } from "common/buildShopLink"
import { setDetail } from "components/shopFeatures/detailing/detailingSlice"
import { useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import ButtonBaseBlank from "reusable/ButtonBaseBlank"
import { useTypedSelector } from "store"
import styled from "styled-components"
import colors from "theme/colors"
import { StyledRow, StyledSvgIcon, TypographyCenter } from "theme/shared"
import PurchaseToolsPopup from "../popups/purchaseTools/PurchaseToolsPopup"
import TestDrivePopup from "../popups/testDrive/TestDrivePopupWrapper"

const StyledWrapper = styled.div`
  background-color: ${(p) => p.theme.colors.transparent};
  position: sticky;
  width: 100%;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: 20px;
`

const StyledCard = styled.div`
  background-color: ${(p) => p.theme.colors.white};
  width: 100%;
  padding: 4px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  max-width: 320px;
  margin: 0 auto;
`

const StyledButtonBaseBlank = styled(ButtonBaseBlank)`
  border-radius: 3px;
  height: 45px;
  width: 100%;
`

const StyledFakeButton = styled.div<{ $dark?: boolean }>`
  background-color: ${(p) =>
    p.$dark ? p.theme.colors.black : p.theme.colors.white};
  border-radius: 3px;
  height: 45px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const StyledLink = styled(NextLink)`
  width: 100%;
  text-align: center;
`

type Props = {
  setPopupOpen?: (falsy: boolean) => void
  numberOfFinishingLevels: number
  modelId: number
  isPreSale: boolean
  isDriveTestAvailable?: boolean
  showroomMeetingUrl?: string
  driveTestUrl?: string
}

const StickyBottomCTA = ({
  setPopupOpen,
  numberOfFinishingLevels,
  modelId,
  isPreSale,
  isDriveTestAvailable,
  showroomMeetingUrl,
  driveTestUrl,
}: Props) => {
  const [testDrivePopupOpen, setTestDrivePopupOpen] = useState(false)
  const [purchaseToolsPopupOpen, setPurchaseToolsPopupOpen] = useState(false)
  const [showRoomsList, setShowRoomList] = useState<any[]>([])
  const [utmParams, setUtmParams] = useState("")
  const [isNavigating, setIsNavigating] = useState(false)

  const detailData = useTypedSelector((state) => state?.detailing)
  const { detailing, color } = detailData || {}

  const router = useRouter()
  const dispatch = useDispatch()
  const shopColorCode = useTypedSelector(
    (state) => state.color.selectedColorCode
  )
  const selectedColorCode = color || shopColorCode
  const detailingsList = detailData?.detailings ?? []
  const carPageDetailingCode = detailData?.detailing || ""
  const findDetailingForColor = async (): Promise<string> => {
    if (!selectedColorCode || detailingsList.length === 0) {
      return carPageDetailingCode
    }

    const candidateCodes = [
      carPageDetailingCode,
      ...detailingsList.map((item) => item.details.code),
    ]
    const checked = new Set<string>()

    for (const code of candidateCodes) {
      if (!code || checked.has(code)) continue
      checked.add(code)

      const finishing = detailingsList.find(
        (item) => item.details.code === code
      )
      if (!finishing?.details?.inStock) continue

      try {
        const {
          data: { body: colorsData },
        } = await getEcommColors(code)
        const matchingColor = colorsData?.find(
          (colorItem) =>
            colorItem?.details?.code === selectedColorCode &&
            colorItem.details?.inStock
        )
        if (matchingColor) {
          return code
        }
      } catch (error) {
        console.error("StickyBottomCTA: unable to fetch colors", error)
      }
    }

    const fallback = detailingsList.find((item) => item.details?.inStock)
      ?.details?.code
    return fallback || carPageDetailingCode
  }
  const handleBuildClick = async () => {
    if (isNavigating) return
    setIsNavigating(true)
    trackBuildMazdaCTA()
    const finalDetailingCode = await findDetailingForColor()
    if (finalDetailingCode) {
      dispatch(setDetail(finalDetailingCode))
    }
    const queryDetailing = finalDetailingCode || carPageDetailingCode
    const url = buildShopLink(
      queryDetailing,
      selectedColorCode,
      modelId,
      utmParams
    )
    try {
      await router.push(url)
    } finally {
      setIsNavigating(false)
    }
  }

  const shopUrl = buildShopLink(detailing, color, modelId, utmParams)

  useEffect(() => {
    let isMounted = true

    const fetchData = async () => {
      try {
        const data = await getShowroomAll(
          null as unknown as number,
          null as unknown as number
        ).then(extractData)
        if (isMounted) setShowRoomList(data?.body ?? [])
      } catch (err) {
        console.error(err)
      }
    }

    fetchData()

    const queryString = window.location.search || ""
    setUtmParams(keepOnlyUtmParams(queryString.replace(/^\?/, "")))

    return () => {
      isMounted = false
    }
  }, [])

  return (
    <>
      <StyledWrapper>
        <StyledCard>
          <StyledRow $fullWidth $gap="2px">
            {isPreSale ? (
              <StyledLink
                href={`/contact-us${utmParams ? `?${utmParams}` : ""}`}
              >
                יצירת קשר
              </StyledLink>
            ) : (
              <StyledButtonBaseBlank
                onClick={() => setPurchaseToolsPopupOpen(true)}
                role="button"
              >
                <StyledFakeButton>
                  <StyledRow $fullWidth $justify="center" $gap="4px">
                    <TypographyCenter variant="body2">
                      לפעולות נוספות
                    </TypographyCenter>
                    <StyledSvgIcon
                      src="/icons/fi_chevron-down.svg"
                      alt="downArrow"
                      $w={18}
                      $h={18}
                    />
                  </StyledRow>
                </StyledFakeButton>
              </StyledButtonBaseBlank>
            )}

            <StyledButtonBaseBlank onClick={handleBuildClick}>
              <StyledFakeButton $dark>
                <TypographyCenter variant="body2" $color={colors.white}>
                  להרכבת המאזדה שלך
                </TypographyCenter>
                <TypographyCenter variant="caption" $color={colors.darkGrey}>
                  {`${numberOfFinishingLevels} רמות גימור לבחירה`}
                </TypographyCenter>
              </StyledFakeButton>
            </StyledButtonBaseBlank>
          </StyledRow>
        </StyledCard>
      </StyledWrapper>

      <PurchaseToolsPopup
        setPopupOpen={setPopupOpen}
        open={purchaseToolsPopupOpen}
        onClose={() => setPurchaseToolsPopupOpen(false)}
        openTestDrivePopup={(openForm) => {
          if (openForm) setTestDrivePopupOpen(true)
          setPurchaseToolsPopupOpen(false)
        }}
        modelId={modelId}
        shopUrl={shopUrl}
        numberOfFinishingLevels={numberOfFinishingLevels}
        isDriveTestAvailable={isDriveTestAvailable}
        showroomMeetingUrl={showroomMeetingUrl}
        driveTestUrl={driveTestUrl}
      />

      <TestDrivePopup
        open={testDrivePopupOpen}
        onClose={() => setTestDrivePopupOpen(false)}
        showRoomsList={showRoomsList}
        modelId={modelId}
        formType="test-drive"
        title="תיאום נסיעת מבחן"
        dialogClass="mobile-margin"
      />
    </>
  )
}

export default StickyBottomCTA
